import { isAbortError } from './utils';

// ═══════════════════════════════════════════════════════════════════════════════
// ERROR DESCRIPTION
//
// Turns an axios rejection into something a person can act on. Every chat
// failure used to surface as the same six words — "Failed to send message" —
// which told the user nothing about whether to retry, wait, or check their wifi.
//
// Two rules hold throughout:
//
//   1. Never invent a cause. If we cannot tell what happened, `kind` is
//      'unknown' and the copy says so rather than guessing "server error".
//   2. `retryable` means retrying could plausibly work. A 400 from a malformed
//      request will fail identically the second time, so it does not offer one.
// ═══════════════════════════════════════════════════════════════════════════════

/** Only trust `detail` if it is a string — FastAPI validation errors send arrays. */
function serverDetail(error) {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    return null;
}

/**
 * @returns {{kind: string, title: string, detail: string, retryable: boolean}}
 */
export function describeError(error, fallback = 'Something went wrong.') {
    if (isAbortError(error)) {
        return { kind: 'aborted', title: 'Stopped', detail: 'The request was cancelled.', retryable: true };
    }

    const status = error?.response?.status;
    const detail = serverDetail(error);

    // No response at all: the request never completed a round trip.
    if (!error?.response) {
        // navigator.onLine is only trustworthy when it says false — a laptop on
        // a captive-portal wifi reports true. So it upgrades the message when
        // false and is otherwise ignored.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return {
                kind: 'offline',
                title: "You're offline",
                detail: 'Reconnect and try again — nothing was sent.',
                retryable: true,
            };
        }
        if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
            return {
                kind: 'timeout',
                title: 'The request timed out',
                detail: 'The model took too long to answer. It may still be working — reload the conversation to check.',
                retryable: true,
            };
        }
        return {
            kind: 'network',
            title: "Couldn't reach the server",
            detail: 'The connection dropped before a reply arrived. Check your network and try again.',
            retryable: true,
        };
    }

    if (status === 401 || status === 403) {
        return {
            kind: 'auth',
            title: 'Your session expired',
            detail: detail || 'Sign in again to continue this conversation.',
            retryable: false,
        };
    }

    if (status === 404) {
        return {
            kind: 'missing',
            title: 'This conversation is gone',
            detail: detail || 'It may have been deleted from another tab.',
            retryable: false,
        };
    }

    if (status === 429) {
        return {
            kind: 'rate_limit',
            title: 'Rate limited',
            detail: detail || 'Too many requests in a short window. Wait a moment, then try again.',
            retryable: true,
        };
    }

    if (status === 402 || status === 413) {
        return {
            kind: 'rejected',
            title: status === 413 ? 'That message is too large' : 'Quota reached',
            detail: detail || fallback,
            retryable: false,
        };
    }

    if (status >= 500) {
        return {
            kind: 'server',
            title: status === 503 ? 'The model is unavailable' : 'The server hit an error',
            detail: detail || 'This is on our side, not yours. Trying again often works.',
            retryable: true,
        };
    }

    if (status >= 400) {
        return {
            kind: 'invalid',
            title: 'That request was rejected',
            detail: detail || fallback,
            retryable: false,
        };
    }

    return { kind: 'unknown', title: 'Something went wrong', detail: detail || fallback, retryable: true };
}

/** One-line form, for toasts where a full card would be too much. */
export function errorMessage(error, fallback) {
    const { title, detail } = describeError(error, fallback);
    return detail ? `${title} — ${detail}` : title;
}
